import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "borderColor", "hasBoxShadow", "showToolbarDivider", "onChatInputChanged", "value", "onKeyDown", "onFocus", "onBlur", "onClick", "disabled", "isInputDisabled", "restrictedPlaceholder", "placeholder", "attachments", "toolbar", "icon", "inputRef", "role", "maxHeight", "shrink", "minHeight", "borderRadius", "hasAnimatedBorder", "contentPaddingBlock"];
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import UIBox from 'UIComponents/layout/UIBox';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming-specialty/utils';
import { css, keyframes } from 'styled-components';
import { BLACK, MAGENTA } from '../../styles/colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const rotateBorderAngle = keyframes(["from{--border-angle:0deg;}to{--border-angle:360deg;}"]);
const BreezeTextInputWrapper = styled(UIBox).withConfig({
  displayName: "AICopilotTextInput__BreezeTextInputWrapper"
})(["border:", ";position:relative;background-color:", " !important;border-radius:", ";box-shadow:", ";", ""], ({
  borderColor,
  theme
}) => `${theme.borderWidth['100']} solid ${borderColor === 'breeze-border-field-default' || borderColor === 'text-core-default' || borderColor === 'border-core-default' || borderColor === 'fill-surface-default' || borderColor === 'breeze-fill-loading-dot-default' || borderColor === 'text-primary-disabled' || borderColor === 'border-core-subtle' || borderColor === 'breeze-fill-secondary-pressed' ? theme.color[borderColor] : borderColor}`, ({
  disabled,
  hasRestrictedPlaceholder,
  theme
}) => disabled || hasRestrictedPlaceholder ? theme.color['fill-field-disabled'] : theme.color['fill-surface-default'], ({
  isTall,
  borderRadius
}) => {
  return borderRadius ? `${borderRadius}px` : isTall ? '12px' : '22px';
}, ({
  hasBoxShadow,
  theme
}) => hasBoxShadow ? theme.boxShadow['100'] : 'none', ({
  hasAnimatedBorder,
  disabled,
  hasRestrictedPlaceholder,
  theme
}) => hasAnimatedBorder && css(["border:1px solid transparent !important;background:linear-gradient( ", ",", " ) padding-box,conic-gradient( from var(--border-angle),transparent 35%,", " 50%,transparent 65% ) border-box !important;animation:", " 2.5s linear infinite;"], disabled || hasRestrictedPlaceholder ? theme.color['fill-field-disabled'] : theme.color['fill-surface-default'], disabled || hasRestrictedPlaceholder ? theme.color['fill-field-disabled'] : theme.color['fill-surface-default'], theme.color['border-accent-magenta-default'], rotateBorderAngle));
const BreezeExpandingTextInput = styled(UIExpandingTextArea).withConfig({
  displayName: "AICopilotTextInput__BreezeExpandingTextInput"
})(["font-size:", ";flex-grow:1;border:none !important;&:focus{box-shadow:none !important;}background-color:", ";color:", ";padding:0;min-height:", ";line-height:", ";&::placeholder{color:", ";}&::-moz-placeholder{color:", ";}&:-ms-input-placeholder{color:", ";}&::-webkit-placeholder{color:", ";}"], ({
  theme
}) => theme.typography['body-125'].fontSize, ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.color['text-core-default'], props => props.hasToolbar ? 'auto' : '22px', props => props.hasToolbar ? 'inherit' : `${props.theme.typography['heading-100'].lineHeight}px`, ({
  theme
}) => theme.color['text-core-subtle'], ({
  theme
}) => theme.color['text-core-subtle'], ({
  theme
}) => theme.color['text-core-subtle'], ({
  theme
}) => theme.color['text-core-subtle']);

// TODO: @Arjun remove the usage of non standard trellis tokens
export const AcceptedColors = [BLACK.NEUTRAL, MAGENTA[400], 'fill-surface-default', 'text-core-default', 'breeze-border-field-default', 'border-core-default', 'breeze-fill-loading-dot-default', 'text-primary-disabled', 'border-core-subtle', 'breeze-fill-secondary-pressed'];
const AICopilotTextInput = _ref => {
  let {
      autoFocus = true,
      borderColor = 'breeze-border-field-default',
      hasBoxShadow = true,
      showToolbarDivider = false,
      onChatInputChanged,
      value,
      onKeyDown,
      onFocus,
      onBlur,
      onClick,
      disabled = false,
      isInputDisabled = false,
      restrictedPlaceholder,
      placeholder,
      attachments,
      toolbar,
      icon,
      inputRef,
      role,
      maxHeight = 200,
      shrink = true,
      minHeight,
      borderRadius,
      hasAnimatedBorder,
      contentPaddingBlock = '200'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const hasToolbar = Boolean(toolbar);
  const hasRestrictedPlaceholder = Boolean(restrictedPlaceholder);
  const hasIconWithoutToolbar = Boolean(icon) && !hasToolbar;
  return /*#__PURE__*/_jsx(Flex, {
    paddingBlockEnd: "200",
    flexDirection: "column",
    inlineSize: "100%",
    children: /*#__PURE__*/_jsxs(BreezeTextInputWrapper, {
      borderColor: borderColor,
      disabled: !!disabled || !!isInputDisabled,
      hasBoxShadow: hasBoxShadow,
      hasToolbar: hasToolbar,
      hasRestrictedPlaceholder: hasRestrictedPlaceholder,
      alignSelf: "stretch",
      isTall: hasToolbar || Boolean(minHeight && minHeight > 28),
      borderRadius: borderRadius,
      hasAnimatedBorder: hasAnimatedBorder,
      children: [hasAnimatedBorder && /*#__PURE__*/_jsx("style", {
        children: `@property --border-angle { syntax: "<angle>"; initial-value: 0deg; inherits: false; }`
      }), !restrictedPlaceholder ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [attachments && /*#__PURE__*/_jsx(_Fragment, {
          children: attachments
        }), /*#__PURE__*/_jsx(View, {
          paddingBlock: contentPaddingBlock,
          paddingInlineStart: "400",
          paddingInlineEnd: hasIconWithoutToolbar ? '1000' : '400',
          children: /*#__PURE__*/_jsx(BreezeExpandingTextInput, Object.assign({}, props, {
            hasToolbar: hasToolbar,
            disabled: !!isInputDisabled,
            autoFocus: autoFocus,
            inputRef: inputRef,
            maxHeight: maxHeight,
            minHeight: minHeight,
            onChange: onChatInputChanged,
            onFocus: onFocus,
            onBlur: onBlur,
            onClick: onClick,
            onKeyDown: onKeyDown,
            placeholder: placeholder,
            resize: false,
            role: role,
            shrink: shrink,
            value: value
          }))
        }), hasToolbar ? /*#__PURE__*/_jsxs(Flex, {
          paddingBlockStart: "200",
          paddingBlockEnd: "200",
          paddingInlineStart: "300",
          paddingInlineEnd: "300",
          borderBlockStartColor: "border-core-default",
          borderBlockStartWidth: showToolbarDivider ? '100' : '0',
          borderBlockStartStyle: "solid",
          alignItems: "center",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx(View, {
            children: toolbar
          }), icon]
        }) : icon]
      }) : /*#__PURE__*/_jsx(Text, {
        color: "text-primary-disabled",
        paddingBlockStart: "200",
        paddingBlockEnd: "200",
        paddingInlineStart: "400",
        paddingInlineEnd: "200",
        children: restrictedPlaceholder
      })]
    })
  });
};
export default AICopilotTextInput;